/**
 * AI 작업 — **무엇을 시킬 수 있나**의 정본. 화면의 목록이자 서버가 프롬프트를 고르는 표다.
 *
 * ⚠️ 새 작업을 더하려면 여기에만 적는다 — 화면은 이 표를 그리고, 서버는 `prompt` 를 읽는다.
 * ⚠️ `needs` 는 그 작업이 **무엇을 대상으로 삼는가**다. 대상이 안 맞으면 화면이 고르지 못하게 막는다.
 */

/**
 * 작업이 무엇을 대상으로 삼나.
 *
 * ```
 *   set           수집 세트(타겟 키워드 캠페인)   조회어 + 사이드바 조건
 *   brand         브랜드 이름 한 줄              본문 검색
 *   account       계정 하나
 *   cart          담은 소재
 *   brandMention  브랜드 멘션 수집분             ⚠️ 화면과 **같은 질문**을 탄다
 *   seeding       시딩 캠페인 하나               ⚠️ 화면과 **같은 질문**을 탄다
 * ```
 * 🚨 `brandMention` 은 `brand` 와 **다르다** — 브랜드 멘션은 태깅된 글에서 자사 계정 글을
 *   빼고 보고, `brand` 는 본문을 검색한다. 수가 다르니 목적에 맞는 쪽을 고른다.
 */
export type AnalyticsJobTarget = 'set' | 'brand' | 'account' | 'cart' | 'brandMention' | 'seeding';

/**
 * **무슨 일을 시키나** — 유형이 대상의 단위와 산출물 목록을 정한다.
 *
 * ```
 *   collect  모은다   링크·계정을 짚어 글을 더 들인다
 *   enrich   판정한다 들인 글 하나하나에 칸을 붙인다
 *   report   읽는다   붙은 값과 수치를 읽어 글 한 장을 낸다
 * ```
 *
 * ⚠️ **알림은 작업이 아니다** — 보고 있는 화면에 거는 설정이라, 그 화면의 사이드바에서
 *   정한다(`components/cmui/filters/alert-setting-button`).
 *
 * 🚨 유형마다 **일의 단위가 다르다** — 수집은 링크 한 줄, 건별 판정은 글 하나,
 *   보고서는 조건 한 벌. 그래서 유형을 먼저 묻고 나머지를 그 안에서 고르게 한다.
 * 🔑 **차례가 곧 파이프라인이다** — 모은 것에만 판정이 붙고, 붙은 값으로만 읽는다.
 * ⚠️ **`report` 만 지시문 표(`ANALYTICS_JOB_KINDS`)를 쓴다** — 나머지 셋은 제 조건 모양을
 *   따로 쥔다(알림은 `types/alert-rule`). 유형마다 ② 걸음의 화면이 다른 까닭이다.
 */
export type AnalyticsJobType = 'collect' | 'enrich' | 'report';

/** **무엇으로 받나** — 보고서 유형 안에서 갈린다(다른 유형은 제 목록을 따로 쥔다) */
export type AnalyticsJobOutput = 'brief' | 'compare' | 'list' | 'digest';

export type AnalyticsJobKind = {
  key: string;
  /** 어느 유형의 일인가 */
  type: AnalyticsJobType;
  /** 무엇으로 받나 */
  output: AnalyticsJobOutput;
  label: string;
  /** 한 줄 설명 — 목록에서 고를 때 읽는다 */
  hint: string;
  icon: string;
  /** 이 작업이 받을 수 있는 대상 */
  needs: AnalyticsJobTarget[];
  /**
   * 모델에게 주는 지시 — 자료는 서버가 붙인다.
   * ⚠️ 「자료에 없으면 모른다고 적어라」를 각 지시가 스스로 말한다(공통 지시만 믿지 않는다).
   */
  prompt: string;
  /** 실물(이미지·영상)이 필요한 작업인가 — 없으면 숫자·글만 싣는다 */
  media?: boolean;
  /**
   * 경쟁 상대까지 **나란히** 볼 작업인가.
   * 🔑 켜면 서버가 후보를 거르고 골라 **브랜드마다 따로** 조회한다 — 조회가 브랜드 수만큼 늘고
   *   고르기 부름이 한 번 더 붙는다. 「누구와 견주나」가 답의 뼈대인 작업에만 켠다.
   */
  rivals?: boolean;
};

export const ANALYTICS_JOB_KINDS: AnalyticsJobKind[] = [
  {
    key: 'video-guideline',
    type: 'report',
    output: 'brief',
    label: '영상 가이드라인 분석',
    hint: '잘된 영상들의 훅·전개·마무리를 뜯어 촬영 가이드로 만든다',
    icon: 'solar:videocamera-record-linear',
    needs: ['cart', 'set', 'brand', 'account'],
    media: true,
    prompt: [
      '주어진 영상 소재들을 촬영 가이드라인으로 정리하세요.',
      '① 0~3초 훅이 무엇인지(화면·말·자막) ② 중간 전개 순서 ③ 마무리와 CTA ④ 반복되는 공통 문법 ⑤ 우리가 따라 만들 때의 체크리스트 6줄.',
      '자료에 없는 장면·수치는 지어내지 말고, 영상이 없으면 그렇게 적으세요.',
    ].join('\n'),
  },
  {
    key: 'creative-structure',
    type: 'report',
    output: 'brief',
    label: '소재 구성 분석',
    hint: '이미지·카피·CTA가 어떻게 짜였는지 비교해 공통점과 차이를 낸다',
    icon: 'solar:gallery-wide-linear',
    needs: ['cart', 'set', 'brand'],
    media: true,
    prompt: [
      '주어진 소재들의 구성을 비교하세요.',
      '① 한눈에 비교하는 표(길이/후킹/스토리/CTA) ② 소재별 뜯어보기 ③ 공통 문법 ④ 갈리는 지점과 그 이유.',
      '표는 마크다운 표로 쓰고, 자료에 없는 수치는 적지 마세요.',
    ].join('\n'),
  },
  {
    key: 'brand-diagnosis',
    type: 'report',
    output: 'brief',
    label: '브랜드 진단',
    hint: '언급 흐름·채널·연관어를 읽어 지금 이 브랜드가 어떤 상태인지 적는다',
    icon: 'solar:chart-square-linear',
    needs: ['set', 'brand'],
    prompt: [
      '주어진 수치로 이 브랜드의 현재 상태를 진단하세요.',
      '① 한 문단 요약 ② 늘고 준 것과 그 크기 ③ 어느 채널·어느 이야기가 끌고 있나 ④ 눈여겨볼 위험 신호 ⑤ 다음 2주에 해 볼 일 3가지.',
      '숫자를 말할 때는 자료에 있는 값만 쓰세요.',
    ].join('\n'),
  },
  {
    key: 'competitor-compare',
    type: 'report',
    output: 'compare',
    rivals: true,
    label: '경쟁 비교',
    hint: '우리와 경쟁 브랜드의 소재·반응을 나란히 놓고 차이를 짚는다',
    icon: 'solar:scale-linear',
    needs: ['cart', 'set', 'brand'],
    media: true,
    prompt: [
      '주어진 소재·수치를 브랜드별로 갈라 비교하세요.',
      '① 브랜드별 한 줄 요약 ② 채널 힘 비교표 — 공식 계정·팔로워·기간 내 자사 글·언급 수',
      '③ 메시지·비주얼·혜택의 차이 ④ 반응이 갈리는 지점 ⑤ 우리가 비어 있는 자리.',
      '🔑 ②는 「브랜드의 얼굴」 표에서 가져오세요 — 언급이 제 채널에서 나온 것인지 남들에게서 나온 것인지가 여기서 갈립니다.',
      '어느 브랜드인지 자료로 가릴 수 없으면 그렇게 적으세요.',
    ].join('\n'),
  },
  {
    key: 'find-creators',
    type: 'report',
    output: 'list',
    label: '크리에이터 찾기',
    hint: '이 브랜드·주제를 실제로 다루는 사람 중 누구와 해 볼지 추린다',
    icon: 'solar:users-group-rounded-linear',
    needs: ['set', 'brand'],
    prompt: [
      '주어진 자료에서 **협업해 볼 크리에이터 후보**를 추리세요.',
      '① 후보 8명 — 계정 · 팔로워 · 왜 이 브랜드에 맞나(근거 한 줄) · 어떤 결의 컨텐츠를 하나',
      '② 먼저 접촉할 3명과 그 차례의 이유 ③ 지금은 빼는 게 나은 유형과 이유.',
      '🔑 **팔로워는 조회해서 적으세요** — 계정 규모를 모르면 누구부터 접촉할지 정할 수 없습니다.',
      '🔑 **그 사람의 글·영상을 직접 보고** 결을 적으세요 — 계정 이름과 글 수만으로는 「어떤 결인지」를 못 말합니다.',
      '⛔ 단가처럼 어디에도 없는 값은 지어내지 말고, 조회해도 모르면 「확인 불가」라고 적으세요.',
      '⚠️ 브랜드 공식 계정·판매몰·매체 계정은 크리에이터가 아니니 가려내고, 가릴 수 없으면 그렇게 적으세요.',
    ].join('\n'),
  },
  {
    key: 'find-competitors',
    type: 'report',
    output: 'list',
    rivals: true,
    label: '경쟁사 찾기',
    hint: '같은 글에 함께 나온 브랜드에서 진짜 경쟁 상대를 가려낸다',
    icon: 'solar:target-linear',
    needs: ['set', 'brand'],
    prompt: [
      '주어진 자료에서 **경쟁 상대 후보**를 가려내세요.',
      '① 후보 표 — 브랜드 · 함께 나온 정도 · 공식 계정과 팔로워 · 기간 내 자사 글 · 어디서 겹치나 · 경쟁으로 볼 근거',
      '② 가장 가까운 셋과 그 이유 ③ 함께 나왔지만 경쟁이 아닌 것과 왜 아닌지.',
      '🔑 **언급량과 자사 채널을 함께 읽으세요** — 「브랜드의 얼굴」 표에 공식 계정·팔로워·자사 글 수가 있습니다.',
      '   자사 글이 많은데 언급이 적으면 제 채널로 밀고 있는 것이고, 자사 글이 적은데 언급이 많으면 남들이 말해 주는 것입니다.',
      '   그 차이를 후보마다 한 줄로 짚으세요.',
      '⚠️ **함께 나왔다고 경쟁이 아니다** — 유통·편집샵·플랫폼 이름, 한 글에 나열된 성분·제품군 이름을 걸러 내세요.',
      '⛔ **자료 밖의 제품명·입점처·라인업을 끌어오지 마세요** — 아는 것을 적는 자리가 아니라 모인 글을 읽는 자리입니다.',
      '근거가 약하면 억지로 넣지 말고 「가릴 수 없다」고 적으세요.',
    ].join('\n'),
  },
  {
    key: 'weekly-brief',
    type: 'report',
    output: 'digest',
    label: '주간 요약',
    hint: '이 조건의 한 주를 브리프 한 장으로 — 사람이 읽고 바로 쓰게',
    icon: 'solar:document-text-linear',
    needs: ['set', 'brand', 'account', 'brandMention', 'seeding'],
    prompt: [
      '이 조건의 최근 한 주를 브리프로 정리하세요.',
      '① 세 줄 요약 ② 숫자로 본 변화 ③ 눈에 띈 글·소재 ④ 다음 주에 볼 것.',
      '마케터가 그대로 공유할 수 있는 말로 쓰고, 분석 용어를 늘어놓지 마세요.',
    ].join('\n'),
  },
  {
    key: 'negative-watch',
    type: 'report',
    output: 'digest',
    label: '부정 반응 감시',
    hint: '불만·부작용 이야기가 늘었는지 보고, 무엇이 문제인지 짚는다',
    icon: 'solar:shield-warning-linear',
    needs: ['set', 'brand', 'brandMention'],
    prompt: [
      '부정적인 이야기를 찾아 정리하세요.',
      '🔑 **부정만 따로 뽑아 보세요** — 최근 글을 그냥 읽으면 그 안에 불만이 한둘이라 「뚜렷하지 않다」로 끝납니다.',
      '① 어떤 불만이 몇 건인지 ② 대표 문장을 **글에서 그대로 인용** ③ 늘고 있는지 ④ 먼저 대응할 것 한 가지.',
      '⚠️ 「부정으로 판별된 것이 0」과 「부정이 0」은 다릅니다 — 판별이 안 붙은 글은 안 들어옵니다. 그 차이를 적으세요.',
      '⚠️ 같은 말이 여러 꼴로 나옵니다(짝퉁·가품·위조품) — 하나로 묶어 세고, 묶었다고 적으세요.',
    ].join('\n'),
  },
  {
    key: 'seeding-review',
    type: 'report',
    output: 'digest',
    label: '시딩 성과 점검',
    hint: '섭외한 계정이 실제로 올렸는지, 누가 얼마나 닿았는지 본다',
    icon: 'solar:clipboard-check-linear',
    needs: ['seeding'],
    prompt: [
      '이 시딩 캠페인의 성과를 점검하세요.',
      '① 한 문단 요약 — 이번 회차가 잘 됐는지 못 됐는지, 근거 숫자와 함께',
      '② 계정별 판정 표 — 계정 · 글 수 · 조회 · 글당 조회 · 한 줄 평(잘함/보통/아쉬움)',
      '③ 성과가 갈린 이유 — 올라온 글에서 읽히는 차이(채널·형식·내용).',
      '   🔑 잘된 것과 안된 것의 **실물을 직접 보고** 무엇이 달랐는지 적으세요 — 수만 봐서는 까닭을 못 말합니다.',
      '④ 다음 회차 — 다시 쓸 계정 · 빼는 게 나은 계정 · 새로 찾을 결',
      '⚠️ 「등록했는데 안 올린 계정」은 자료에 그대로 적혀 있습니다 — 그 목록을 빠뜨리지 말고,',
      '   거기 없는 계정을 미이행이라고 적지 마세요.',
      '⛔ 단가·계약 조건은 자료에 없습니다 — 비용 대비 효율을 지어내지 마세요.',
    ].join('\n'),
  },
  {
    key: 'mention-brief',
    type: 'report',
    output: 'brief',
    label: '브랜드 멘션 브리프',
    hint: '우리 브랜드가 이번 기간에 어디서 어떻게 말해졌는지 한 장으로',
    icon: 'solar:chat-square-like-linear',
    needs: ['brandMention'],
    prompt: [
      '이 기간에 우리 브랜드가 어떻게 말해졌는지 브리프로 정리하세요.',
      '① 세 줄 요약 ② 숫자로 본 상태(글·계정·반응, 채널이 어떻게 갈리나)',
      '③ 무슨 이야기로 말해지나 — 성분·피부 고민·해시태그에서 읽히는 것',
      '④ 누가 말하나 — 많이 쓴 계정의 결 ⑤ 부정 반응이 있으면 무엇이고 대응할 것인지',
      '⑥ 다음 2주에 해 볼 일 3가지.',
      '마케터가 그대로 공유할 수 있는 말로 쓰고, 자료에 없는 수치는 적지 마세요.',
    ].join('\n'),
  },
  {
    key: 'content-idea',
    type: 'report',
    output: 'list',
    label: '컨텐츠 아이디어',
    hint: '지금 반응이 좋은 이야기에서 다음에 만들 것을 뽑는다',
    icon: 'solar:lightbulb-bolt-linear',
    needs: ['set', 'brand', 'cart'],
    prompt: [
      '주어진 자료에서 다음에 만들 컨텐츠를 제안하세요.',
      '① 지금 잘 먹히는 이야기 3가지와 근거 ② 그것을 우리 식으로 바꾼 기획안 5개(후킹 한 줄 포함) ③ 피할 것.',
      '근거 없는 유행은 적지 마세요.',
    ].join('\n'),
  },
];

/**
 * **유형 셋** — 화면이 맨 처음 묻는 것.
 * 🚨 `ready` 가 거짓인 유형은 **목록에서 빼지 않는다** — 빼면 「왜 없나」를 알 길이 없다.
 *   막힌 까닭(`blocked`)을 화면이 그대로 적는다.
 * ⚠️ 건별 분석의 판독기와 생성 상류는 **이 저장소에 없다** — 수집 백엔드의 일이다.
 */
export const ANALYTICS_JOB_TYPES: {
  key: AnalyticsJobType;
  label: string;
  hint: string;
  icon: string;
  /** 지금 돌릴 수 있나 */
  ready: boolean;
  /** 못 돌리는 까닭 — 화면이 그대로 적는다 */
  blocked?: string;
}[] = [
  {
    key: 'collect',
    label: '데이터 추가 수집',
    hint: '링크나 계정을 짚어 글을 더 들인다 — 시딩 포스팅, 특정 계정의 글',
    icon: 'solar:inbox-in-linear',
    ready: false,
    blocked: '지금은 화면마다 흩어져 있습니다 — 시딩 포스팅은 캠페인의 「포스팅 추가」, 계정은 추적 관리에서 등록합니다. 이 화면에서 바로 받는 기능은 준비 중입니다.',
  },
  {
    key: 'enrich',
    label: '데이터 건별 분석 추가',
    hint: '글 하나하나에 판정값을 붙여, 그 값으로 거르고 집계할 수 있게 한다',
    icon: 'solar:layers-minimalistic-linear',
    ready: false,
    blocked: '판독기는 수집 백엔드에 있습니다. 창구·어휘·하루 처리량 계약을 물어 두었고 회신을 기다리는 중입니다.',
  },
  {
    key: 'report',
    label: '보고서 작성',
    hint: '모은 자료를 읽고 사람이 읽을 글 한 장을 낸다',
    icon: 'solar:document-text-linear',
    ready: true,
  },
];

/**
 * **산출물 형태 넷** — 보고서 유형 안에서 고른다.
 * 🔑 형태와 대상이 정해지면 쓸 지시문이 좁혀진다(`src/sections/jobs/scope/job-recipe-scope.ts`) —
 *   후보가 여럿이면 화면이 한 줄 더 묻는다.
 */
export const ANALYTICS_JOB_OUTPUTS: { key: AnalyticsJobOutput; label: string; hint: string; icon: string }[] = [
  { key: 'brief', label: '브리프', hint: '지금 상태를 한 장으로 — 읽고 바로 공유한다', icon: 'solar:document-text-linear' },
  { key: 'compare', label: '비교 표', hint: '둘 이상을 나란히 놓고 차이를 짚는다', icon: 'solar:scale-linear' },
  { key: 'list', label: '목록', hint: '후보를 추려 줄마다 이름과 고른 까닭을 적는다', icon: 'solar:list-check-linear' },
  { key: 'digest', label: '정기 요약', hint: '같은 조건을 거듭 보며 지난번과의 차이를 짚는다', icon: 'solar:calendar-linear' },
];

/** 예약 주기 — 예약은 «적어 두는 것»이고 실행은 수집 백엔드의 일이다 */
export const ANALYTICS_JOB_SCHEDULES = [
  { key: 'once', label: '지금 한 번' },
  { key: 'daily', label: '매일' },
  { key: 'weekly', label: '매주' },
] as const;

export function findJobKind(key: string): AnalyticsJobKind | null {
  return ANALYTICS_JOB_KINDS.find((kind) => kind.key === key) ?? null;
}
